import React, { useState } from "react";
import ASSETS from "../assets.json";
import "./WorkflowSection.scss";

const {
    imgProduct:          IMG_PRODUCT,
    imgScene:            IMG_SCENE,
    imgGenerate:         IMG_GENERATE,
    imgRefine:           IMG_REFINE,
    imgReview:           IMG_REVIEW,
    imgPublish:          IMG_PUBLISH,
    imgShopify:          IMG_SHOPIFY,
    imgAmazon:           IMG_AMAZON,
    imgCtaBedroom:       IMG_BED,
    imgSceneInspiration: IMG_SCENE_INSP,
    imgSceneProps:       IMG_SCENE_PROPS,
    imgSwatchWood:       IMG_SWATCH_WOOD,
    imgRefineScene:      IMG_REFINE_SCENE,
    imgRefineUI:         IMG_REFINE_UI,
    imgRefineGreenMask:  IMG_REFINE_MASK,
    imgReviewR1A:        IMG_REVIEW_R1A,
    imgReviewR1B:        IMG_REVIEW_R1B,
    imgReviewR1C:        IMG_REVIEW_R1C,
    imgReviewR2A:        IMG_REVIEW_R2A,
    imgReviewR2B:        IMG_REVIEW_R2B,
    imgReviewR2C:        IMG_REVIEW_R2C,
    imgReviewIconCheck:   IMG_REVIEW_CHECK,
    imgReviewIconPending: IMG_REVIEW_PENDING,
    imgReviewIconReject:  IMG_REVIEW_REJECT,
    imgReviewIconChevron: IMG_REVIEW_CHEVRON,
    imgPublishBg:         IMG_PUBLISH_BG,
    imgShopifyInverse:    IMG_SHOPIFY_INV,
    imgAmazonInverse:     IMG_AMAZON_INV,
    imgGenerateSlide1:    IMG_GENERATE_1,
    imgGenerateSlide2:    IMG_GENERATE_2,
    imgGenerateSlide3:    IMG_GENERATE_3,
} = ASSETS.workflow;

// ─── Detail panel position (row-relative) per step ───────────────────────────
// Panel is always top:0, height:400 within the active row.
// Row has `position: relative; height: 400px` when active.
const PANEL_POS = [
    { left: 0,   width: 520 }, // step 1 — expanded 520px (wider than sm card per Figma)
    { left: 480, right: 0, minWidth: 880 }, // step 2 — 880px min per Figma
    { left: 0,   width: 700 }, // step 3 — 700px per Figma
    { left: 660, right: 0, minWidth: 700 }, // step 4 — 700px min per Figma
    { left: 0,   width: 520 }, // step 5 — 520px per Figma
    { left: 480, right: 0, minWidth: 880 }, // step 6 — 880px min per Figma
];

// ─── Per-step detail content ──────────────────────────────────────────────────
const STEP_INFO = [
    { title: "Add Product",          desc: "Upload a product photo and dimensions.", img: IMG_PRODUCT, isProduct: true, isScene: false },
    { title: "Build a Scene",        desc: "Add an inspiration image, props, and guide the look to match your brand.", img: IMG_PRODUCT, isScene: true  },
    { title: "Generate 4K Visuals",  desc: "Generate stunning 4K visuals, accurate to real-world space.", img: IMG_GENERATE, isGenerate: true, isScene: false },
    { title: "Refine & Scale",       desc: "Edit your images easily—scale or replace items, magic erase, and more.", subDesc: "And, for even more precision use our spatial 3D feature.", img: IMG_REFINE, isRefine: true, isScene: false },
    { title: "Review & Collaborate", desc: "Review and collaborate on assets with your team.", img: IMG_REVIEW, isReview: true, isScene: false },
    { title: "Publish to Store",     desc: "Use our Shopify and Amazon integrations or simply download your assets.", img: IMG_PUBLISH, isPublish: true, isScene: false },
];

// ─── Detail panel (rendered inside the active row) ────────────────────────────
function DetailPanel({ stepIdx, onClose }) {
    const pos  = PANEL_POS[stepIdx];
    const info = STEP_INFO[stepIdx];

    const panelStyle = {
        top:    0,
        left:   pos.left,
        height: 400,
        ...(pos.minWidth !== undefined ? { minWidth: pos.minWidth } : {}),
        ...(pos.right !== undefined ? { right: pos.right } : { width: pos.width }),
    };

    if (info.isProduct) {
        const dims = [
            { label: "Width",  value: "14" },
            { label: "Height", value: "18" },
            { label: "Depth",  value: "12" },
        ];
        return (
            <div className="lv2-wf-detail-panel lv2-wf-detail-product" style={panelStyle}>
                <img className="lv2-wf-detail-product-img" src={info.img} alt="Product photo for upload" />
                <div className="lv2-wf-detail-dims">
                    {dims.map(({ label, value }) => (
                        <div key={label} className="lv2-wf-detail-dim-group">
                            <span className="lv2-wf-detail-dim-label">{label}</span>
                            <div className="lv2-wf-detail-dim-input">
                                <span className="lv2-wf-detail-dim-value">{value}</span>
                                <span className="lv2-wf-detail-dim-unit">in</span>
                            </div>
                        </div>
                    ))}
                </div>
                <p className="lv2-wf-detail-product-desc">{info.desc}</p>
            </div>
        );
    }

    if (info.isScene) {
        // Step 2 — Build a Scene expanded state
        return (
            <div className="lv2-wf-detail-panel lv2-wf-detail-scene-v2" style={panelStyle}>
                {/* Large inspiration scene image */}
                <div className="lv2-wf-scene2-insp-large">
                    <img src={IMG_SCENE_INSP} alt="Scene inspiration image" />
                </div>
                {/* Smaller props card overlapping */}
                <div className="lv2-wf-scene2-props-card">
                    <img src={IMG_SCENE_PROPS} alt="Scene props reference" />
                </div>
                {/* Color / texture swatches column */}
                <div className="lv2-wf-scene2-swatches">
                    <div className="lv2-wf-scene2-swatch lv2-wf-scene2-swatch-beige" />
                    <div className="lv2-wf-scene2-swatch lv2-wf-scene2-swatch-wood">
                        <img src={IMG_SWATCH_WOOD} alt="Wood texture swatch" />
                    </div>
                    <div className="lv2-wf-scene2-swatch lv2-wf-scene2-swatch-dark" />
                </div>
                {/* Product image */}
                <img className="lv2-wf-scene2-product" src={info.img} alt="Product placed in scene" />
                {/* Description */}
                <p className="lv2-wf-scene2-desc">{info.desc}</p>
            </div>
        );
    }

    if (info.isReview) {
        // Step 5 — Review & Collaborate: two marquee rows (top→left, bottom→right)
        const topRow = [
            { src: IMG_REVIEW_R1A, status: "Approved", icon: IMG_REVIEW_CHECK   },
            { src: IMG_REVIEW_R1B, status: "Pending",  icon: IMG_REVIEW_PENDING },
            { src: IMG_REVIEW_R1C, status: "Pending",  icon: IMG_REVIEW_PENDING },
        ];
        const bottomRow = [
            { src: IMG_REVIEW_R2A, status: "Rejected", icon: IMG_REVIEW_REJECT },
            { src: IMG_REVIEW_R2B, status: "Approved", icon: IMG_REVIEW_CHECK  },
            { src: IMG_REVIEW_R2C, status: "Rejected", icon: IMG_REVIEW_REJECT },
        ];
        const renderItem = ({ src, status, icon }, i) => (
            <div key={i} className="lv2-wf-review-item">
                <img className="lv2-wf-review-img" src={src} alt={`${status} asset`} />
                <div className="lv2-wf-review-tag">
                    <img className="lv2-wf-review-tag-icon" src={icon} alt={status} />
                    <span className="lv2-wf-review-tag-label">{status}</span>
                    <img className="lv2-wf-review-tag-chevron" src={IMG_REVIEW_CHEVRON} alt="" />
                </div>
            </div>
        );
        return (
            <div className="lv2-wf-detail-panel lv2-wf-detail-review" style={panelStyle}>
                {/* Top row — scrolls left */}
                <div className="lv2-wf-review-strip lv2-wf-review-strip--top">
                    <div className="lv2-wf-review-track lv2-wf-review-track--left">
                        {[...topRow, ...topRow].map(renderItem)}
                    </div>
                </div>
                {/* Bottom row — scrolls right */}
                <div className="lv2-wf-review-strip lv2-wf-review-strip--bottom">
                    <div className="lv2-wf-review-track lv2-wf-review-track--right">
                        {[...bottomRow, ...bottomRow].map(renderItem)}
                    </div>
                </div>
                {/* Bottom gradient */}
                <div className="lv2-wf-review2-fade" />
                {/* Description */}
                <p className="lv2-wf-review2-desc">{info.desc}</p>
            </div>
        );
    }

    if (info.isGenerate) {
        // Step 3 — Generate 4K Visuals: 3-image Ken Burns crossfade slideshow
        const slides = [
            { src: IMG_GENERATE_1, cls: "lv2-wf-gen-slide--1" },
            { src: IMG_GENERATE_2, cls: "lv2-wf-gen-slide--2" },
            { src: IMG_GENERATE_3, cls: "lv2-wf-gen-slide--3" },
        ];
        return (
            <div className="lv2-wf-detail-panel lv2-wf-detail-generate" style={panelStyle}>
                {slides.map(({ src, cls }) => (
                    <img key={cls} className={`lv2-wf-gen-slide ${cls}`} src={src} alt="AI-generated 4K product visual" />
                ))}
                <div className="lv2-wf-gen-fade" />
                <p className="lv2-wf-gen-desc">{info.desc}</p>
            </div>
        );
    }

    if (info.isRefine) {
        // Step 4 — Refine & Scale: image in → green mask → UI panel
        return (
            <div className="lv2-wf-detail-panel lv2-wf-detail-refine" style={panelStyle}>
                {/* Scene image card with green mask overlay */}
                <div className="lv2-wf-refine-scene-card">
                    <img className="lv2-wf-refine-scene-img" src={IMG_REFINE_SCENE} alt="Scene before refinement" />
                    <img className="lv2-wf-refine-green-mask" src={IMG_REFINE_MASK} alt="Green selection mask for editing" />
                </div>
                {/* Refine UI panel */}
                <img className="lv2-wf-refine-ui" src={IMG_REFINE_UI} alt="Refine and scale editing UI panel" />
                {/* Text */}
                <p className="lv2-wf-refine-desc-primary">{info.desc}</p>
                <p className="lv2-wf-refine-desc-secondary">{info.subDesc}</p>
            </div>
        );
    }

    if (info.isPublish) {
        // Step 6 — Publish to Store: bg texture drifts; headline fades out; logos + desc fade in
        return (
            <div className="lv2-wf-detail-panel lv2-wf-detail-publish" style={panelStyle}>
                {/* Starfield texture — subtly drifting */}
                <img className="lv2-wf-publish-bg" src={IMG_PUBLISH_BG} alt="" />
                {/* Headline — fades up and out */}
                <p className="lv2-wf-publish-headline">Publish to Store</p>
                {/* Store logos — scale + fade in */}
                <img className="lv2-wf-publish-logo lv2-wf-publish-logo-shopify" src={IMG_SHOPIFY_INV} alt="Shopify" />
                <img className="lv2-wf-publish-logo lv2-wf-publish-logo-amazon"  src={IMG_AMAZON_INV}  alt="Amazon" />
                {/* Description — fades up in */}
                <p className="lv2-wf-publish-desc">{info.desc}</p>
            </div>
        );
    }

    // Other steps — full-bleed zoomed image with label at bottom-left
    return (
        <div className="lv2-wf-detail-panel lv2-wf-detail-zoom" style={panelStyle}>
            <img className="lv2-wf-detail-zoom-img" src={info.img} alt={info.title} />
            <div className="lv2-wf-detail-zoom-overlay" />
            <p className="lv2-wf-detail-zoom-label">{info.title}</p>
        </div>
    );
}

// ─── Component ────────────────────────────────────────────────────────────────
const WorkflowSection = () => {
    const [activeStep, setActiveStep] = useState(null);

    // Which row (0/1/2) contains the active step — steps 0-1 = row 0, 2-3 = row 1, 4-5 = row 2
    const activeRow = activeStep !== null ? Math.floor(activeStep / 2) : -1;

    const handleClick = (i) => setActiveStep(prev => prev === i ? null : i);

    const rowCls = (rowIdx) =>
        `lv2-wf-row${activeRow === rowIdx ? " lv2-wf-row--active" : ""}`;

    return (
        <div className="lv2-wf-section">
        <div className="lv2-wf-wrapper">

            {/* ── Heading ──────────────────────────────────────────────────────── */}
            <div className="lv2-wf-heading">
                <h2 className="lv2-wf-title">A workflow built for teams</h2>
                <p className="manrope f-20 w-500 lh-28 lv2-wf-subtitle">
                    Create product images, videos, and assets with an AI workflow built for ecommerce teams.
                </p>
            </div>

            {/* ── Steps grid ───────────────────────────────────────────────────── */}
            <div className="lv2-wf-steps">

                {/* Row 1 ─ Add Product + Build a Scene */}
                <div className={rowCls(0)}>

                    {/* Step 1 — Add Product */}
                    <div className="lv2-wf-card lv2-wf-card-sm" onClick={() => handleClick(0)}>
                        <img className="lv2-wf-img lv2-wf-img-product" src={IMG_PRODUCT} alt="Add product photo" />
                        <span className="lv2-wf-num lv2-wf-num-front" style={{ top: "6px", color: "rgba(0,0,0,0.15)" }}>1</span>
                        <h3 className="lv2-wf-label lv2-wf-label-dark lv2-wf-label-30">Add Product</h3>
                    </div>

                    {/* Step 2 — Build a Scene */}
                    <div className="lv2-wf-card lv2-wf-card-lg" onClick={() => handleClick(1)}>
                        <img className="lv2-wf-img lv2-wf-img-scene" src={IMG_SCENE} alt="Build a scene" />
                        <div className="lv2-wf-overlay" />
                        <span className="lv2-wf-num lv2-wf-num-front lv2-wf-num-center" style={{ color: "rgba(255,255,255,0.3)" }}>2</span>
                        <h3 className="lv2-wf-label lv2-wf-label-white lv2-wf-label-60">Build a Scene</h3>
                    </div>

                    {/* Detail panel for row 1 */}
                    {activeRow === 0 && (
                        <DetailPanel key={activeStep} stepIdx={activeStep} onClose={() => setActiveStep(null)} />
                    )}

                </div>

                {/* Row 2 ─ Generate 4K Visuals + Refine & Scale */}
                <div className={rowCls(1)}>

                    {/* Step 3 — Generate 4K Visuals */}
                    <div className="lv2-wf-card lv2-wf-card-md" onClick={() => handleClick(2)}>
                        <img className="lv2-wf-img lv2-wf-img-generate" src={IMG_GENERATE} alt="Generate 4K visuals" />
                        <span className="lv2-wf-num lv2-wf-num-front lv2-wf-num-center" style={{ color: "rgba(255,255,255,0.3)" }}>3</span>
                        <h3 className="lv2-wf-label lv2-wf-label-white lv2-wf-label-60">
                            Generate<br />4K Visuals
                        </h3>
                    </div>

                    {/* Step 4 — Refine & Scale */}
                    <div className="lv2-wf-card lv2-wf-card-md" onClick={() => handleClick(3)}>
                        <img className="lv2-wf-img lv2-wf-img-refine" src={IMG_REFINE} alt="Refine and scale assets" />
                        <div className="lv2-wf-overlay lv2-wf-overlay--dark" />
                        <span className="lv2-wf-num lv2-wf-num-front lv2-wf-num-center" style={{ color: "rgba(255,255,255,0.3)" }}>4</span>
                        <h3 className="lv2-wf-label lv2-wf-label-white lv2-wf-label-60">Refine &amp; Scale</h3>
                    </div>

                    {/* Detail panel for row 2 */}
                    {activeRow === 1 && (
                        <DetailPanel key={activeStep} stepIdx={activeStep} onClose={() => setActiveStep(null)} />
                    )}

                </div>

                {/* Row 3 ─ Review & Collaborate + Publish to Store */}
                <div className={rowCls(2)}>

                    {/* Step 5 — Review & Collaborate */}
                    <div className="lv2-wf-card lv2-wf-card-sm lv2-wf-card-end" onClick={() => handleClick(4)}>
                        <img className="lv2-wf-img lv2-wf-img-review" src={IMG_REVIEW} alt="Review and collaborate on assets" />
                        <div className="lv2-wf-review-fade" />
                        <span className="lv2-wf-num lv2-wf-num-front lv2-wf-num-center" style={{ color: "rgba(255,255,255,0.4)" }}>5</span>
                        <h3 className="lv2-wf-label lv2-wf-label-white lv2-wf-label-30 lv2-wf-label-shadow-sm">
                            Review &amp; Collaborate
                        </h3>
                    </div>

                    {/* Step 6 — Publish to Store */}
                    <div className="lv2-wf-card lv2-wf-card-lg lv2-wf-card-black" onClick={() => handleClick(5)}>
                        <img className="lv2-wf-img lv2-wf-img-publish" src={IMG_PUBLISH} alt="Publish products to Shopify and Amazon storefronts" />
                        <span className="lv2-wf-num lv2-wf-num-front lv2-wf-num-center" style={{ color: "rgba(255,255,255,0.2)" }}>6</span>
                        <h3 className="lv2-wf-label lv2-wf-label-white lv2-wf-label-60 lv2-wf-label-shadow-sm">
                            Publish to Store
                        </h3>
                        <img className="lv2-wf-shopify" src={IMG_SHOPIFY} alt="Shopify" />
                        <img className="lv2-wf-amazon"  src={IMG_AMAZON}  alt="Amazon" />
                    </div>

                    {/* Detail panel for row 3 */}
                    {activeRow === 2 && (
                        <DetailPanel key={activeStep} stepIdx={activeStep} onClose={() => setActiveStep(null)} />
                    )}

                </div>

            </div>
        </div>{/* end lv2-wf-wrapper */}

        {/* ── Pricing CTA banner ───────────────────────────────────────────── */}
        <div className="lv2-wf-cta-banner">
            <img className="lv2-wf-cta-img" src={IMG_BED} alt="" />
            <div className="lv2-wf-cta-fade" />
            <div className="lv2-wf-cta-content">
                <div className="lv2-wf-cta-text">
                    <h3 className="lv2-wf-cta-heading">
                        How much does this AI-generated asset workflow cost?
                    </h3>
                    <p className="manrope f-16 w-400 lh-24 white">
                        We offer credit-based packages starting at only $20/month and work with individuals, small businesses, and enterprise solutions to fit any budget.
                    </p>
                </div>
                <button className="lv2-wf-cta-btn manrope f-16 w-500 lh-24 white">
                    View plans &amp; pricing
                </button>
            </div>
        </div>

        </div>
    );
};

export default WorkflowSection;
